<template>
  <el-dialog :title="$t('waves.steps.create')" :visible.sync="open" :before-close="close">
    <div class="mb-4 flex items-center">
      <p class="mr-2">What is the name of the step?</p>
      <div class="flex-1">
        <input
          v-model="title"
          type="text"
          placeholder="Welcome Alfred, you're now a new member"
          class="w-full focus:outline-none focus:text-gray-600 p-2 border rounded"
        />
      </div>
    </div>

    <div>
      <div class="mb-4 flex items-center">
        <p class="mr-2">Which mode do you want to use?</p>
        <div class="flex-1">
          <select v-model="type" name="type" class="border rounded p-2 cursor-pointer">
            <option value="popup">Popup</option>
            <option value="tour">Tour</option>
          </select>
        </div>
      </div>

      <div v-if="type === 'tour'" class="mb-4 flex items-center">
        <p class="mr-2">What is the target?</p>
        <div class="flex-1">
          <input
            v-model="target"
            type="text"
            @change="checkCssId()"
            placeholder="Target element must be a css ID prefix with #"
            class="w-full focus:outline-none focus:text-gray-600 p-2 border rounded"
          />
          <div id="target-input-error">{{ targetErrorMessage }}</div>
        </div>
      </div>

      <div v-if="type === 'tour'" class="mb-4 flex items-center">
        <p class="mr-2">Where do you want to place the content?</p>
        <div class="flex-1">
          <select v-model="position" name="position" class="border rounded p-2 cursor-pointer">
            <option value="top">Top</option>
            <option value="right">Right</option>
            <option value="bottom">Bottom</option>
            <option value="left">Left</option>
          </select>
        </div>
      </div>

      <div class="mb-2">
        <p class="font-bold mb-4">What is the heading?</p>
        <div class="mt-2">
          <label
            v-for="lang in languages"
            :key="lang"
            class=" cursor-pointer w-full mb-2 inline-flex items-center ml-6"
          >
            <span class="ml-2 w-1/12 inline-block">{{ lang }}</span>
            <input
              v-model="heading[lang]"
              type="text"
              placeholder="Heading of the step"
              class="w-11/12 mx-4 px-3 py-3 placeholder-gray-400 text-gray-700 relative bg-white bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline"
            />
          </label>
        </div>
      </div>

      <k-tabs>
        <k-tab v-for="lang in languages" :key="lang" :name="lang" :selected="lang === defaultLang">
          <editor
            :apiKey="tinyemceApiKey"
            v-model="body[lang]"
            :init="{
              height: 300,
              menubar: false,
              plugins: [
                'emoticons advlist autolink lists link image charmap',
                'searchreplace visualblocks code fullscreen',
                'print preview anchor insertdatetime',
                'paste code help wordcount table'
              ],
              toolbar: [
                'formatselect h1 h2 fontselect fontsizeselect bold italic underline strikethrough',
                'backcolor forecolor | alignleft aligncenter alignright alignjustify | table image | styleselect emoticons code'
              ],
              images_upload_handler: uploadImage
            }"
          >
          </editor>
        </k-tab>
      </k-tabs>
    </div>

    <import-fields v-if="csvFields.length > 0" v-bind:fields="csvFields" />

    <div class="flex flex-row-reverse mt-4">
      <button @click="save()" class="px-8 py-2 rounded bg-yellow-500 text-blue-50 max-w-max shadow-sm hover:shadow-md">
        Save
      </button>
      <button
        @click="close()"
        class="px-8 py-2 rounded border border-gray-200 text-gray-500 max-w-max shadow-sm hover:shadow-md mr-3"
      >
        cancel
      </button>
    </div>
  </el-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import LibraryManager from '../../../libraries/library-manager.vue'
import Editor from '@tinymce/tinymce-vue'
import ImportFields from '../../../common/import-fields.vue'

export default {
  name: 'FormWaveStep',

  components: { LibraryManager, Editor, ImportFields },

  props: {
    open: {
      type: Boolean,
      required: true
    },
    step: {
      type: Object,
      default: null,
      required: false
    },
    csvFields: {
      type: Array
    }
  },
  data() {
    return {
      type: 'popup',
      position: 'top',
      target: null,
      heading: {},
      title: null,
      body: {},
      targetErrorMessage: ''
    }
  },
  computed: {
    tinyemceApiKey() {
      return process.env.TINYMCE_API_KEY
    },

    ...mapGetters('account', {
      defaultLang: 'defaultLang',
      site: 'currentSite'
    }),
    languages() {
      return this.site.languages.map(lang => lang.lang)
    }
  },
  methods: {
    ...mapActions('medias', ['addWaveMedia']),

    getImg(index) {
      if (this.fields.step[index].media) {
        return this.fields.step[index].media.url
      }
    },

    imgSelected(media) {
      this.fields.step.find(item => item.type === 'image').media = media

      this.libraryOpened = !this.libraryOpened
    },
    save() {
      // Prepare object to send
      let step = {
        type: this.type,
        title: this.title,
        heading: this.heading,
        body: this.body
      }

      if (step.type === 'tour') {
        step.position = this.position
        step.target = this.target
      }

      if (this.step) {
        step.index = this.step.index
      }

      this.$emit('created-step', step)
      this.close()
    },
    close() {
      this.$emit('close', true)
    },
    uploadImage(blobInfo, success, failure, folderName) {
      this.addWaveMedia({ media: blobInfo.blob() })
        .then(media => {
          success(media.url)
        })
        .catch(response => {
          this.$notify.error({
            title: this.$root.$t('api.' + response.code + '.title'),
            message: this.$root.$t('api.' + response.code + '.message')
          })
          failure('Error uploading image')
        })
    },
    checkCssId() {
      let value = this.target
      if (value.length > 0 && value.charAt(0) !== '#') {
        this.targetErrorMessage = 'css id must start with #'
      } else {
        this.targetErrorMessage = ''
      }
    }
  },
  mounted() {
    if (this.step) {
      this.type = this.step.type
      this.step.position === null ? (this.position = 'top') : (this.position = this.step.position)
      this.target = this.step.target
      this.title = this.step.title
      this.languages.forEach(lang => {
        if (this.step.heading[lang]) {
          this.$set(this.heading, lang, this.step.heading[lang])
        } else {
          this.$set(this.heading, lang, '')
        }
        if (this.step.body[lang]) {
          this.$set(this.body, lang, this.step.body[lang])
        } else {
          this.$set(this.body, lang, '')
        }
      })
    }
  }
}
</script>

<style>
.tox.tox-silver-sink.tox-tinymce-aux {
  z-index: 10000;
}
#target-input-error {
  color: #ff6961;
}
</style>



// WEBPACK FOOTER //
// src/components/waves/ui/modal/step-create.vue